import "typeface-roboto";

import * as api from "./redux/api/actions.js";

import { AVAILABLE_ENTITIES, utils } from "./database";
import React, { useEffect, Suspense } from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  withRouter,
} from "react-router-dom";
import { getLoggedUser, login } from "./redux/auth/actions.js";
import { makeStyles, useTheme } from "@material-ui/core/styles";

// ============================================================================
// HYBRID LAZY LOADING STRATEGY (Sprint 2)
// ============================================================================
// Monitor para instrumentação e debugging de lazy loading
import { monitorLazyLoad, setupNetworkMonitoring } from "./utils/lazyLoadMonitor";

// ========== PÁGINAS ADMIN (LAZY) ==========
// Páginas administrativas que NÃO precisam funcionar offline
// OK falhar quando offline - usuário receberá Error Boundary
const AdminAddTypeDomain = monitorLazyLoad(() => import("./pages/AdminAddTypeDomain/AdminAddTypeDomain.js"), 'AdminAddTypeDomain');
const AdminAddValueDomain = monitorLazyLoad(() => import("./pages/AdminAddValueDomain/AdminAddValueDomain.js"), 'AdminAddValueDomain');
const AdminReceiptReleases = monitorLazyLoad(() => import("./pages/AdminReceiptReleases/AdminReceiptReleases.js"), 'AdminReceiptReleases');
const AdminReleasesList = monitorLazyLoad(() => import("./pages/AdminReleasesList/AdminReleasesList.js"), 'AdminReleasesList');

// ========== PÁGINAS CORE (EAGER) ==========
// Páginas essenciais que DEVEM funcionar 100% OFFLINE
// CRÍTICO: Carregadas imediatamente para garantir disponibilidade offline

// Batches (Lotes) - CORE offline
import Batch from "./pages/batches/Batch.js";
import BatchCreate from "./pages/batches/Create.js";
import BatchMerge from "./pages/batches/Merge.js";
import BatchUpdate from "./pages/batches/Update.js";

// Bulls (Touros) - CORE offline
import Bull from "./pages/bulls/Bull.js";
import BullCreate from "./pages/bulls/Create.js";
import BullCreateForAll from "./pages/bulls/CreateForAll.js";
import BullList from "./pages/bulls/List.js";
import BullUpdate from "./pages/bulls/Update.js";

// Corrals (Retiros) - CORE offline
import Corral from "./pages/corrals/Corral";
import CorralCreate from "./pages/corrals/Create.js";
import CorralUpdate from "./pages/corrals/Update.js";

// IATF - CORE da aplicação (usado offline no curral)
import D0 from "./pages/IATF/D0";
import D0AfterResync from "./pages/IATF/D0AfterResync";
import D0IATF from "./pages/IATF/D0IATF";
import D0Resync from "./pages/IATF/D0Resync";
import DG from "./pages/IATF/DG";
import DGFinal from "./pages/IATF/DGFinal";
import DGRessinc from "./pages/IATF/DGRessinc";
import DN from "./pages/IATF/DN";
import IATF from "./pages/IATF/IATF";
import Resync from "./pages/IATF/Resync";

// Estação de Monta - CORE offline
import EstacaodeMonta from "./pages/EstacaoDeMontaOtimizada";

// Farms (Fazendas) - CORE offline
import Farm from "./pages/farms/Farm.js";
import FarmCreate from "./pages/farms/Create.js";
import FarmUpdate from "./pages/farms/Update";

// Partidas - CORE offline
import Partidas from "./pages/partidas/Partida.js";

// Protocols (Protocolos) - CORE offline
import Protocol from "./pages/protocols/Protocol";
import ProtocolCreate from "./pages/protocols/Create";
import ProtocolUpdate from "./pages/protocols/Update";
import ProtocolsList from "./pages/protocols/List";

// Home - CORE offline
import Home from "./pages/Home";

// ========== PÁGINAS OPCIONAIS (LAZY) ==========
// Páginas que NÃO precisam funcionar offline
// Carregadas sob demanda para otimizar bundle inicial

// Importação de dados (admin only)
const ImportData = monitorLazyLoad(() => import("./pages/ImportData/ImportData"), 'ImportData');

// Relatórios (não usados no curral)
const NewReport = monitorLazyLoad(() => import("./pages/new-reports/NewReport"), 'NewReport');
const Report = monitorLazyLoad(() => import("./pages/reports/Report"), 'Report');

// Treinamentos (opcional)
const Trainings = monitorLazyLoad(() => import("./pages/Trainings/Training.js"), 'Trainings');

// Dashboard Geral (online-only)
const DashboardGeral = monitorLazyLoad(() => import("./pages/DashboardGeral/DashboardGeral.js"), 'DashboardGeral');

// Usuários (geralmente gerenciados online)
const UserCreate = monitorLazyLoad(() => import("./pages/Users/Create.js"), 'UserCreate');
const UserList = monitorLazyLoad(() => import("./pages/Users/List.js"), 'UserList');
const UserUpdate = monitorLazyLoad(() => import("./pages/Users/Update.js"), 'UserUpdate');

// Componentes carregados imediatamente (sempre necessários)
import { Alert } from "@material-ui/lab";
import BackgrounImage from "./assets/img/background.jpg";
import Box from "@material-ui/core/Box";
import Container from "@material-ui/core/Container";
import CssBaseline from "@material-ui/core/CssBaseline";
import LoadingOverlay from "./components/LoadingOverlay";
import LazyErrorBoundary from "./components/LazyErrorBoundary";
import Prompt from "./components/Prompt.js";
import SyncView from "./components/SyncView";
import OfflineIndicator from "./components/OfflineIndicator";
import { TextField } from "@material-ui/core";
import { ThemeProvider } from "@material-ui/styles";
import clsx from "clsx";
import { connect } from "react-redux";
import { verify } from "./utils/verifyDuplicatePages";

const drawerWidth = 240;

export const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  appBar: {
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    borderColor: "#EA5D0D",
  },
  appBarShift: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  right: {
    position: "absolute",
    height: "100%",
    right: "1rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  hide: {
    display: "none",
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
    border: 0,
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: "flex-end",
    borderColor: "#EA5D0D",
    backgroundColor: "#EA5D0D",
    width: drawerWidth,
  },
  content: {
    flexGrow: 1,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: -drawerWidth,
  },
  contentShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  },

  selectFarm: {
    marginRight: "2em",
    lineHeight: "50%",
    // marginTop: -20,
  },
}));

function App({ logged, title, ...props }) {
  const classes = useStyles();
  const theme = useTheme();
  const [open] = React.useState(true);
  const { location } = useHistory();
  const [newPassword, setNewPassword] = React.useState("");

  // Inicializar monitor de lazy loading
  useEffect(() => {
    setupNetworkMonitoring();

    // Expor função de stats no console para debugging
    console.log('[APP] Lazy Load Stats disponíveis via: window.__LAZY_LOAD_STATS_LOG__()');

    return () => {
      // Cleanup se necessário
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div
      className="app-wrapper-bg"
      style={{ backgroundImage: "url(" + BackgrounImage + ")" }}
    >
      <LoadingOverlay />
      <OfflineIndicator />
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container className={classes.root} disableGutters>
          <CssBaseline />

          <main
            className={clsx(classes.content, {
              [classes.contentShift]: open,
            })}
          >
            {/* Error Boundary para recuperação de falhas de chunk lazy */}
            <LazyErrorBoundary>
              {/* Suspense para lazy loading de rotas */}
              <Suspense fallback={<LoadingOverlay />}>
                <Switch>
              {/* Home / Farm List */}
              <Route exact path="/" component={Home} />
              {/* Dashboard Geral Tecnico (online-only) */}
              <Route exact path="/dashboard-geral" component={DashboardGeral} />
              {/* Estação de monta otimizada */}
              <Route
                exact
                path="/farms/estacaodemonta"
                component={EstacaodeMonta}
              />
              {/* Farms */}
              <Route exact path="/farms/create" component={FarmCreate} />
              <Route exact path="/farms/:id" component={Farm} />
              <Route exact path="/farms/:id/update" component={FarmUpdate} />
              <Route exact path="/farms/:farmId/report" component={NewReport} />
              <Route
                exact
                path="/farms/:farmId/new-report"
                component={Report}
              />
              {/* Users */}
              <Route exact path="/users" component={UserList} />
              <Route exact path="/users/create" component={UserCreate} />
              <Route exact path="/users/:id" component={UserUpdate} />
              {/* Protocols */}
              <Route exact path="/protocols" component={ProtocolsList} />
              <Route
                exact
                path="/protocols/create"
                component={ProtocolCreate}
              />
              <Route exact path="/protocols/:id" component={Protocol} />
              <Route
                exact
                path="/protocols/:id/update"
                component={ProtocolUpdate}
              />
              {/* Bulls */}
              <Route exact path="/farms/:id/bulls" component={BullList} />
              <Route
                exact
                path="/farms/:id/bulls/create"
                component={BullCreate}
              />
              <Route exact path="/farms/:id/bulls/:bullId" component={Bull} />
              <Route
                exact
                path="/farms/:id/bulls/:bullId/update"
                component={BullUpdate}
              />
              <Route
                exact
                path="/bulls/create-for-all/batch/:batchId/management/:management"
                component={BullCreateForAll}
              />
              {/* Corrals */}
              <Route
                exact
                path="/farms/:farmId/corrals/create"
                component={CorralCreate}
              />
              <Route
                exact
                path="/farms/:farmId/corrals/:id"
                component={Corral}
              />
              <Route
                exact
                path="/farms/:farmId/corrals/:id/update"
                component={CorralUpdate}
              />
              <Route
                exact
                path="/farms/:farmId/corrals/:corralId/report"
                component={NewReport}
              />
              <Route
                exact
                path="/farms/:farmId/corrals/:corralId/new-report"
                component={Report}
              />
              {/* Batches */}
              <Route
                exact
                path="/farms/:farmId/corrals/:corralId/mergeBatches"
                component={BatchMerge}
              />
              <Route
                exact
                path="/farms/:farmId/corrals/:corralId/batches/:id/update"
                component={BatchUpdate}
              />
              <Route
                exact
                path="/farms/:farmId/corrals/:corralId/batches/create"
                component={BatchCreate}
              />
              <Route
                exact
                path="/farms/:farmId/corrals/:corralId/batches/:id"
                component={Batch}
              />
              <Route
                exact
                path="/farms/:farmId/corrals/:corralId/batches/:batchId/report"
                component={NewReport}
              />
              <Route
                exact
                path="/farms/:farmId/corrals/:corralId/batches/:batchId/new-report"
                component={Report}
              />
              {/* Cadastro de partidas */}
              <Route
                exact
                path="/partidas/:bullId/batch/:batchId/management/:management"
                component={Partidas}
              />
              {/* Importar dados */}
              <Route exact path="/importar-dados" component={ImportData} />
              {/* Treinamentos */}
              <Route exact path="/treinamentos" component={Trainings} />
              {/* Administrador lancamentos*/}
              <Route
                exact
                path="/lancamentos-do-administrador"
                component={AdminReleasesList}
              />
              {/* Adicionar Valor Dominio */}
              <Route
                exact
                path="/lancamentos-de-valores-dominio"
                component={AdminAddValueDomain}
              />
              {/* Adicionar Tipo Dominio */}
              <Route
                exact
                path="/lancamentos-de-tipos-dominio"
                component={AdminAddTypeDomain}
              />
              {/* Recuperar dados do comprovante */}
              <Route
                exact
                path="/lancamentos-de-comprovantes"
                component={AdminReceiptReleases}
              />
              {/* Processes */}
              <Route exact path="/D0IATF/:id" component={D0IATF} />
              <Route
                exact
                path="/D0IATF/:id/redirected/:redirected"
                component={D0IATF}
              />
              <Route exact path="/D0/:id" component={D0} />
              <Route exact path="/D0Resync/:id" component={D0Resync} />
              <Route
                exact
                path="/D0AfterResync/:id"
                component={D0AfterResync}
              />
              <Route exact path="/DN/:id" component={DN} />
              <Route exact path="/DG/:id/" component={DG} />
              <Route exact path="/DGRessinc/:id/" component={DGRessinc} />
              <Route exact path="/DGFinal/:id" component={DGFinal} />
              <Route exact path="/IATF/:id" component={IATF} />
              <Route
                exact
                path="/IATF/:id/redirected/:redirected"
                component={IATF}
              />
              <Route exact path="/Resync/:id" component={Resync} />
              <Redirect to="/" />
              </Switch>
              </Suspense>
            </LazyErrorBoundary>
            <Container style={{ marginTop: "4rem", marginBottom: "4rem" }}>
              {" "}
            </Container>
          </main>
        </Container>
        <Box displayPrint="none">
          <SyncView />
        </Box>
        <Prompt
          visible={!props?.loggedIn && props?.loginError === "Senha incorreta!"}
          title="Detectamos uma mudança na senha associada à sua conta em outro dispositivo. Para continuar desfrutando dos nossos serviços, solicitamos que insira a sua senha atual no campo abaixo."
          message={
            <div>
              {props?.error && (
                <Alert severity="error" style={{ marginTop: "1em" }}>
                  {props?.error.title}
                </Alert>
              )}
              <div className="p-12 d-4 p-margin-top-2 grid-item">
                <TextField
                  id="new-password"
                  label="Senha"
                  name="password"
                  variant="outlined"
                  type="password"
                  onChange={(e) => setNewPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      e.shiftKey === false &&
                      e.target.value !== ""
                    ) {
                      e.preventDefault();

                      props.login({
                        login: props?.user.email,
                        password: newPassword,
                        isNewPassword: true,
                      });
                    }
                  }}
                  style={{
                    width: "100%",
                    backgroundColor: "white",
                  }}
                />
              </div>
            </div>
          }
          buttons={[
            {
              label: "Salvar",
              onClick: () =>
                props?.login({
                  login: props?.user.email,
                  password: newPassword,
                  isNewPassword: true,
                }),
            },
          ]}
        />

        <Prompt
          visible={verify()}
          title="Página duplicada!"
          message={`Para evitar uma possível perda de dados, não é permitido abrir o Progerar em mais de uma aba!
            Para continuar utilizando, feche esta página e volte para utilizar a anterior.`}
          buttons={[
            {
              autoFocus: false,
            },
          ]}
        />
      </ThemeProvider>
    </div>
  );
}

const propsFromDatabase = async (props) => {
  const loggedUser = await getLoggedUser();

  // Se não há loggedUser ou é primeiro login, não buscar user da database
  if (!loggedUser || !loggedUser.userId || loggedUser.isFirstLogin) {
    return {
      user: null,
    };
  }

  const user = await utils.getElement(
    AVAILABLE_ENTITIES.USERS,
    loggedUser.userId,
  );

  return {
    user,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    login: (params) => dispatch(login(params, true)),
  };
};

const mapStateToProps = (state) => {
  return {
    error: api.getApiError(state, `Authentication/RefreshToken`) || false,
    success: api.getApiResult(state, `Authentication/RefreshToken`) || false,
    loggedIn: state.auth.loggedIn,
    loginError: state.auth.loginError,
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(utils.withPropsFromDatabase(propsFromDatabase, withRouter(App)));
